import { useEffect, useMemo, useState } from 'react';
import { useFirebase } from 'containers/FirebaseContext';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { BINGO_THEME_SONG } from 'utils/config/reference';

const AUDIO_COLLECTION = import.meta.env.VITE_APP_STAGE?.trim() === 'prod' ? 'audio_gallery_prod' : 'audio_gallery';

const normalizeSongGenres = (data) => {
  if (Array.isArray(data?.genres) && data.genres.length) {
    return data.genres.map((g) => String(g || '').trim()).filter(Boolean);
  }
  const single = String(data?.genre || '').trim();
  return single ? [single] : ['Uncategorized'];
};

const mapStatic = () =>
  BINGO_THEME_SONG.map((s) => ({
    url: s.url,
    title: s.title,
    genres: ['Themes'],
  }));

const uniqueSortedGenres = (songList) => {
  const set = new Set();
  (songList || []).forEach((s) => {
    (s.genres || []).forEach((g) => {
      if (g) set.add(g);
    });
  });
  return [...set].sort((a, b) => a.localeCompare(b));
};

export function useBingoThemeSongs() {
  const { firestore } = useFirebase();
  const [remoteList, setRemoteList] = useState(undefined);

  useEffect(() => {
    if (!firestore) {
      setRemoteList(null);
      return;
    }
    const q = query(collection(firestore, AUDIO_COLLECTION), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((x) => x.url && typeof x.url === 'string')
          .map((x) => {
            const genres = normalizeSongGenres(x);
            const genrePart = genres.join(', ');
            return {
              url: x.url,
              title: String(x.fileName || genrePart || '').trim() || 'Audio',
              genres,
            };
          });
        setRemoteList(list);
      },
      () => setRemoteList([])
    );
    return () => unsub();
  }, [firestore]);

  const ready = useMemo(() => {
    if (!firestore) return true;
    return remoteList !== undefined;
  }, [firestore, remoteList]);

  const songs = useMemo(() => {
    if (!firestore) return mapStatic();
    if (remoteList === undefined) return mapStatic();
    if (Array.isArray(remoteList) && remoteList.length > 0) return remoteList;
    return mapStatic();
  }, [firestore, remoteList]);

  const genres = useMemo(() => uniqueSortedGenres(songs), [songs]);

  return { songs, genres, ready };
}
